import { useMemo, useState } from 'react';
import { AcoesTriagem, BotaoCancelar, BotaoManter } from '@/components/kanban/triagem/AcoesTriagem';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Check, Clock, Eye, Loader2, Mail, MessageSquare, UserX, Wrench, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { mensagemDaFuncao } from '@/lib/erroDaFuncao';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';
import { Button } from '@/components/ui/button';
import { ManterComNotaModal } from '@/components/kanban/ManterComNotaModal';
import { CancelarItemModal } from '@/components/pedido/CancelarItemModal';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';
import { TriagemShell } from '@/components/kanban/triagem/TriagemShell';
import { ehDoSegmento, formatarDocumento, rotuloSegmento, useSegmentoTriagem } from '@/components/kanban/triagem/segmentoTriagem';
import {
  TRIAGEM_BTN, TRIAGEM_BTN_ATIVO, TRIAGEM_BTN_PRIMARIO, TRIAGEM_CELULA, TRIAGEM_CELULA_FORTE, TRIAGEM_CELULA_NUM,
  TRIAGEM_TD, TRIAGEM_TH, TRIAGEM_TR_BASE, TRIAGEM_TR_LIVRE, TRIAGEM_TR_ZEBRA,
} from '@/components/kanban/triagem/triagemTabela';
import { CopyOnClick } from '@/components/planilha/CopyOnClick';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { InputarFibraDialog } from '@/components/kanban/fluxo/InputarFibraDialog';
import { chamarInstalacao } from '@/lib/rotaInstalacao';
import { fetchPedidoContato } from '@/lib/fetchPedidoContato';
import { TtEmailPopover } from './TtEmailPopover';
import { enderecoTtCompleto, faltaParaEmailTt, type DadosTt, type EnderecoTt } from './ttFixaDados';
import { agendaInstalacaoTexto } from '@/lib/turnoInstalacao';
import { cn } from '@/lib/utils';
import { rotuloPrazo, useSlaPrazos } from '@/hooks/useSla';
import { equipesDoLider, filtrarPeloLider } from '../triagem/triagemPedidoUtils';
import { useUserProfileContext } from '@/contexts/UserProfileContext';
import { depoisDoClique, manterNaTriagem, marcarTelasDeForaDesatualizadas, useLinhasOcupadas } from '../triagem/acaoTriagem';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface ItemInstalacao {
  item_id: string;
  pedido_id: string;
  numero_pedido: string;
  cnpj: string;
  documento: string;
  tipo_pessoa: string | null;
  razao_social: string;
  produto: string;
  consultor: string;
  data_agendamento: string | null;
  data_input: string | null;
  entrada_coluna_em: string;
  status_id: string;
  coluna_atual: string;
  quadro_atual: string;
  cliente_id: string;
  telefone: string;
  email: string;
  contrato: string;
}

interface PedidoInstalacao {
  pedido_id: string;
  agenda: string | null;
  chegada: string | null;
  soTt: boolean;
  itens: ItemInstalacao[];
}

const TZ = 'America/Sao_Paulo';
const diaBr = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: TZ });

function situacao(iso: string | null) {
  if (!iso) return { texto: 'sem data', rotulo: 'marque a data', vencida: true, dias: 0 };
  const d = new Date(iso);
  const hoje = diaBr(new Date());
  const dia = diaBr(d);
  const dias = Math.round((new Date(hoje).getTime() - new Date(dia).getTime()) / 86_400_000);
  const texto = agendaInstalacaoTexto(iso);
  if (dias > 0) return { texto, rotulo: `${dias} ${dias === 1 ? 'dia' : 'dias'} vencida`, vencida: true, dias };
  if (dias === 0) return { texto, rotulo: 'hoje', vencida: false, dias };
  return { texto, rotulo: dias === -1 ? 'amanhã' : `em ${-dias} dias`, vencida: false, dias };
}

/** Horas de segunda a sexta (dia inteiro), a mesma régua de fn_horas_semana. */
function horasSemana(inicio: Date, fim: Date): number {
  let total = 0;
  const d = new Date(inicio);
  while (d < fim) {
    const prox = new Date(Math.min(fim.getTime(), new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime()));
    const dia = d.getDay();
    if (dia !== 0 && dia !== 6) total += (prox.getTime() - d.getTime()) / 3_600_000;
    d.setTime(prox.getTime());
  }
  return total;
}

/* O turno da fibra (maquete v7, Douglas 30/10/2026): o técnico tem 1 h depois
   do fim do turno; com 2 h sem tratar, a BKO trava. A régua mora no banco
   (fn_instalacao_situacao, fn_sla_fila); aqui só se lê. */
interface EstadoFibra {
  pedido_id: string;
  agenda: string | null;
  fim: string | null;
  turno_aviso_em: string | null;
  cliente_instalado_em: string | null;
  bko_falou_em: string | null;
  travou_em: string | null;
  tecnico_nao_veio_em: string | null;
  ficha_status: 'pendente' | 'enviando' | 'enviada' | 'falhou' | null;
  ficha_em: string | null;
  ficha_erro: string | null;
  conversa_desde: string | null;
  conversa_bko_em: string | null;
  conversa_bko_texto: string | null;
  conversa_cliente_em: string | null;
  conversa_cliente_texto: string | null;
}

interface PlacarInstalacao { mes: string; agendadas: number; instalou_1o_turno: number; tecnico_nao_veio: number; nova_data_cliente?: number; bko_falou_min: number | null }

const HORA_MS = 3_600_000;

function quando(iso: string) {
  const d = new Date(iso);
  const hora = d.toLocaleTimeString('pt-BR', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
  if (diaBr(d) === diaBr(new Date())) return `às ${hora}`;
  return `em ${d.toLocaleDateString('pt-BR', { timeZone: TZ, day: '2-digit', month: '2-digit' })} às ${hora}`;
}

type TomFibra = 'neutro' | 'pendente' | 'ok' | 'danger';

function situacaoFibra(p: PedidoInstalacao, est?: EstadoFibra): { principal: string; detalhe: string; tom: TomFibra; vencida: boolean } {
  if (!p.agenda) return { principal: 'Sem data', detalhe: 'a data entra pelo Inputar', tom: 'pendente', vencida: true };
  const base = situacao(p.agenda);
  if (!est?.fim) return { principal: base.rotulo, detalhe: base.texto, tom: base.vencida ? 'pendente' : 'neutro', vencida: base.vencida };
  const agora = Date.now();
  const agenda = new Date(est.agenda ?? p.agenda).getTime();
  const fim = new Date(est.fim).getTime();
  if (est.tecnico_nao_veio_em) {
    const dias = Math.floor((agora - new Date(est.tecnico_nao_veio_em).getTime()) / 86_400_000);
    const ficha = est.ficha_status === 'enviada' && est.ficha_em ? `ficha no grupo ${quando(est.ficha_em)}`
      : est.ficha_status === 'falhou' ? `a ficha não saiu: ${est.ficha_erro || 'tente de novo'}` : 'a ficha está saindo para o grupo';
    return {
      principal: dias >= 1 ? `Esperando a nova data da Claro há ${dias} ${dias === 1 ? 'dia' : 'dias'}` : 'Esperando a nova data da Claro',
      detalhe: ficha, tom: est.ficha_status === 'falhou' ? 'danger' : 'pendente', vencida: false,
    };
  }
  if (est.cliente_instalado_em) {
    return { principal: `O cliente respondeu que foi instalado, ${quando(est.cliente_instalado_em)}`, detalhe: 'confira e finalize', tom: 'ok', vencida: false };
  }
  if (est.conversa_cliente_em && (agora < fim || !est.travou_em)) {
    return {
      principal: `O cliente respondeu ${quando(est.conversa_cliente_em)}`,
      detalhe: agora < fim ? 'pode lançar a nova data' : 'finalize, registre o Técnico não veio ou lance a nova data',
      tom: 'pendente', vencida: agora >= fim + HORA_MS,
    };
  }
  if (est.conversa_bko_em && agora < fim) {
    return { principal: `A BKO falou com o cliente ${quando(est.conversa_bko_em)}`, detalhe: 'a nova data libera quando ele responder', tom: 'neutro', vencida: false };
  }
  if (agora < agenda) {
    return base.rotulo === 'hoje'
      ? { principal: 'Agendado · hoje', detalhe: `o turno abre ${quando(est.agenda ?? p.agenda)}`, tom: 'neutro', vencida: false }
      : { principal: 'Agendado · ainda não é o dia', detalhe: base.rotulo, tom: 'neutro', vencida: false };
  }
  if (agora < fim) {
    return { principal: 'Em instalação agora', detalhe: `o turno vai até ${quando(est.fim)}`, tom: 'neutro', vencida: false };
  }
  const falou = est.bko_falou_em && (!est.travou_em || est.bko_falou_em > est.travou_em) ? est.bko_falou_em : null;
  if (falou) return { principal: `A BKO falou com o cliente ${quando(falou)}`, detalhe: 'finalize ou registre o Técnico não veio', tom: 'pendente', vencida: false };
  if (est.travou_em) {
    return { principal: 'Passou 2 h do turno e o cliente não respondeu', detalhe: `travou ${quando(est.travou_em)}`, tom: 'danger', vencida: true };
  }
  if (agora >= fim + HORA_MS) {
    return { principal: 'Passou 1 h do turno e o cliente não respondeu', detalhe: `trava ${quando(new Date(fim + 2 * HORA_MS).toISOString())}`, tom: 'pendente', vencida: true };
  }
  return { principal: `O turno acabou ${quando(est.fim)}`, detalhe: `o técnico ainda tem até ${quando(new Date(fim + HORA_MS).toISOString()).replace(/^às /, '')}`, tom: 'neutro', vencida: false };
}

const TOM_FIBRA: Record<TomFibra, string> = {
  neutro: 'text-foreground',
  pendente: 'text-primary dark:text-[#C87484]',
  ok: 'text-[#075E54] dark:text-[#25D366]',
  danger: 'text-danger',
};

const PRAZO_TT_HORAS = 48;

// TT Fixa (Douglas 22/10/2026): o relógio de 48 h recomeça a cada e-mail à gerente de contas.
interface EstadoTt { pedido_id: string; enviados: number; ultimo_em: string | null; falhou: boolean }

function situacaoTt(chegada: string | null, estado?: EstadoTt) {
  const desde = estado?.ultimo_em && (!chegada || estado.ultimo_em > chegada) ? estado.ultimo_em : chegada;
  if (!desde) return { texto: 'sem data', rotulo: 'TT Fixa · 48 h', vencida: false };
  const h = Math.floor(horasSemana(new Date(desde), new Date()));
  const texto = new Date(chegada || desde).toLocaleDateString('pt-BR', { timeZone: TZ, day: '2-digit', month: '2-digit' });
  const emails = estado?.enviados ? `${estado.enviados}º e-mail · ` : 'sem e-mail · ';
  return { texto, rotulo: `${emails}${h} h de 48 h`, vencida: h > PRAZO_TT_HORAS || !estado?.enviados };
}

async function chamarTtFixa<T>(path: string, body: unknown): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sua sessão expirou. Entre novamente.');
  const base = ((import.meta.env.VITE_SUPABASE_URL as string) || 'https://beta.crm-api.primetecia.com.br/api').replace(/\/$/, '');
  const r = await fetch(`${base}/v1/instalacao/tt-fixa/${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  const corpo = await r.json().catch(() => ({ error: 'O CRM não respondeu corretamente.' }));
  if (!r.ok) throw new Error(corpo.error || 'Não foi possível concluir.');
  return corpo.data as T;
}

type Rpc = (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: Error | null }>;

export function TriagemEmInstalacaoModal({ isOpen, onClose }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useUserProfileContext();
  const { open: abrirPedido } = usePainelPedido();
  const [aba, setAba] = useState<'fibra' | 'tt'>('fibra');
  const [novaDataDe, setNovaDataDe] = useState<PedidoInstalacao | null>(null);
  const [novaDataPeloCliente, setNovaDataPeloCliente] = useState(false);
  const [naoVeioDe, setNaoVeioDe] = useState<PedidoInstalacao | null>(null);
  const [manter, setManter] = useState<PedidoInstalacao | null>(null);
  const [cancelar, setCancelar] = useState<ItemInstalacao | null>(null);
  const { refetchInterval, onQueryError, onQuerySuccess } = useSmartPolling({ weight: 'B', baseInterval: POLLING_INTERVALS.PESO_B });

  /* A triagem clica e faz (`triagem/acaoTriagem.ts`): a linha sai ou desce
     assim que o banco confirma, só o pedido clicado fica ocupado, e o Kanban e
     a Planilha ficam marcados para se lerem quando aparecerem. */
  const { ocupada, ocupar } = useLinhasOcupadas();
  const naLinha = (pedidoId: string, fn: () => Promise<unknown>) => { void ocupar(pedidoId, fn).catch(() => undefined); };
  const depois = (pedidoId: string, modo: 'sai' | 'desce') => {
    void depoisDoClique(queryClient, ['triagem-em-instalacao'], { pedidoId }, modo);
    queryClient.invalidateQueries({ queryKey: ['triagem-solar'] });
  };
  /* A nova data não tira nem desce: a fila se reordena pela data nova quando
     se relê. */
  const releFila = (pedidoId: string) => {
    ['triagem-em-instalacao', 'triagem-solar'].forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
    queryClient.invalidateQueries({ queryKey: ['timeline-pedido', pedidoId] });
    marcarTelasDeForaDesatualizadas(queryClient);
  };

  const { data: itensTodos = [], isLoading, refetch } = useQuery({
    queryKey: ['triagem-em-instalacao'],
    enabled: isOpen,
    refetchInterval,
    queryFn: async (): Promise<ItemInstalacao[]> => {
      const lider = await equipesDoLider(profile);
      const { data, error } = await filtrarPeloLider(supabase
        .from('v_planilha_mae')
        .select('item_id, pedido_id, numero_pedido, cnpj, documento, tipo_pessoa, razao_social, produto, consultor_nome, data_agendamento, data_input, entrada_coluna_em, status_id, coluna_atual, quadro_atual, cliente_id, telefone_principal, contato_real_email, numero_cotacao_operadora')
        .eq('quadro_atual', 'TRAMITACAO_VIVO')
        .eq('coluna_atual', 'EM INSTALAÇÃO'), profile, lider);
      if (error) { onQueryError(error); throw error; }
      onQuerySuccess();
      return ((data || []) as Record<string, unknown>[]).map((r) => ({
        item_id: String(r.item_id),
        pedido_id: String(r.pedido_id),
        numero_pedido: String(r.numero_pedido ?? ''),
        cnpj: String(r.cnpj ?? ''),
        documento: String(r.documento ?? r.cnpj ?? ''),
        tipo_pessoa: (r.tipo_pessoa as string) ?? null,
        razao_social: String(r.razao_social ?? ''),
        produto: String(r.produto ?? ''),
        consultor: String(r.consultor_nome ?? '').split(' ')[0],
        data_agendamento: (r.data_agendamento as string) ?? null,
        data_input: (r.data_input as string) ?? null,
        entrada_coluna_em: String(r.entrada_coluna_em ?? ''),
        status_id: String(r.status_id ?? ''),
        coluna_atual: String(r.coluna_atual ?? 'EM INSTALAÇÃO'),
        quadro_atual: String(r.quadro_atual ?? 'TRAMITACAO_VIVO'),
        cliente_id: String(r.cliente_id ?? ''),
        telefone: String(r.telefone_principal ?? ''),
        email: String(r.contato_real_email ?? ''),
        contrato: String(r.numero_cotacao_operadora ?? ''),
      }));
    },
  });

  const { segmento, setSegmento } = useSegmentoTriagem();

  const pedidos = useMemo(() => {
    const porPedido = new Map<string, PedidoInstalacao>();
    for (const it of itensTodos) {
      if (!ehDoSegmento(it.tipo_pessoa, segmento)) continue;
      const p = porPedido.get(it.pedido_id) ?? { pedido_id: it.pedido_id, agenda: null, chegada: null, soTt: true, itens: [] };
      p.itens.push(it);
      if (it.produto !== 'TT FIXA') p.soTt = false;
      if (it.data_agendamento && (!p.agenda || it.data_agendamento < p.agenda)) p.agenda = it.data_agendamento;
      if (it.data_input && (!p.chegada || it.data_input < p.chegada)) p.chegada = it.data_input;
      porPedido.set(it.pedido_id, p);
    }
    const chave = (p: PedidoInstalacao) => (p.soTt ? p.chegada : p.agenda) ?? '';
    return [...porPedido.values()].sort((a, b) => chave(a).localeCompare(chave(b)) || a.pedido_id.localeCompare(b.pedido_id));
  }, [itensTodos, segmento]);

  const fibra = pedidos.filter((p) => !p.soTt);
  const tt = pedidos.filter((p) => p.soTt);
  const idsTt = tt.map((p) => p.pedido_id);
  const { data: estadosTt = [] } = useQuery({
    queryKey: ['tt-fixa-estado', idsTt.join(',')],
    enabled: isOpen && idsTt.length > 0,
    staleTime: 30_000,
    queryFn: () => chamarTtFixa<EstadoTt[]>('estado', { pedidoIds: idsTt }),
  });
  const estadoTt = new Map(estadosTt.map((e) => [e.pedido_id, e]));
  const { data: prazosSla } = useSlaPrazos(isOpen ? 'EM INSTALAÇÃO' : null);
  // O Contrato PF e o endereço das TT (Douglas 24/10/2026): a linha diz o que falta
  // para o e-mail sair e o envelope deixa preencher ali mesmo.
  const idsItensTt = tt.flatMap((p) => p.itens.map((i) => i.item_id));
  const { data: linhasTt = [] } = useQuery({
    queryKey: ['tt-fixa-dados', idsItensTt.join(',')],
    enabled: isOpen && idsItensTt.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('itens_pedido').select('id, pedido_id, tt_contrato_pf, endereco_instalacao').in('id', idsItensTt);
      if (error) throw error;
      return (data || []) as { id: string; pedido_id: string; tt_contrato_pf: string | null; endereco_instalacao: EnderecoTt | null }[];
    },
  });
  const dadosTt = new Map<string, DadosTt>();
  for (const l of linhasTt) {
    const d = dadosTt.get(l.pedido_id) ?? { contratoPf: '', endereco: {} };
    if (!d.contratoPf && l.tt_contrato_pf?.trim()) d.contratoPf = l.tt_contrato_pf.trim();
    if (l.endereco_instalacao && (enderecoTtCompleto(l.endereco_instalacao) || !enderecoTtCompleto(d.endereco))) d.endereco = l.endereco_instalacao;
    dadosTt.set(l.pedido_id, d);
  }
  const idsFibra = fibra.map((p) => p.pedido_id);
  const { data: estadosFibra = [] } = useQuery({
    queryKey: ['instalacao-acompanhamento', idsFibra.join(',')],
    enabled: isOpen && idsFibra.length > 0,
    staleTime: 30_000,
    refetchInterval: isOpen ? 60_000 : false,
    queryFn: () => chamarInstalacao<EstadoFibra[]>('acompanhamento/estado', 'POST', { pedidoIds: idsFibra }),
  });
  const estadoFibra = new Map(estadosFibra.map((e) => [e.pedido_id, e]));
  const { data: placar } = useQuery({
    queryKey: ['instalacao-placar'],
    enabled: isOpen,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: () => chamarInstalacao<PlacarInstalacao | null>('placar', 'GET'),
  });
  const situacaoPedido = (p: PedidoInstalacao) => {
    if (p.soTt) return situacaoTt(p.chegada, estadoTt.get(p.pedido_id));
    const f = situacaoFibra(p, estadoFibra.get(p.pedido_id));
    return { texto: agendaInstalacaoTexto(p.agenda), rotulo: f.principal, vencida: f.vencida };
  };
  const [emailDe, setEmailDe] = useState<string | null>(null);
  const emailTt = useMutation({
    mutationFn: async ({ pedido, novos }: { pedido: PedidoInstalacao; novos: DadosTt | null }) => {
      if (novos) {
        const { error } = await supabase.from('itens_pedido')
          .update({ tt_contrato_pf: novos.contratoPf || null, endereco_instalacao: novos.endereco } as never)
          .in('id', pedido.itens.map((i) => i.item_id));
        if (error) throw new Error(`Não salvou o contrato PF e o endereço: ${error.message}`);
        queryClient.invalidateQueries({ queryKey: ['tt-fixa-dados'] });
      }
      return chamarTtFixa<{ numero: number }>(`${pedido.pedido_id}/email`, {});
    },
    onSuccess: (r, { pedido }) => {
      toast({ title: `${r.numero}º e-mail enviado à gerente de contas`, description: 'O pedido foi para o fim da fila. Cobrança de novo em 48 h.' });
      setEmailDe(null);
      queryClient.invalidateQueries({ queryKey: ['tt-fixa-estado'] });
      depois(pedido.pedido_id, 'desce');
    },
    onError: (e) => toast({ title: 'O e-mail não saiu', description: e instanceof Error ? e.message : 'Tente novamente.', variant: 'destructive' }),
  });
  const lista = aba === 'fibra' ? fibra : tt;
  const vencidas = pedidos.filter((p) => situacaoPedido(p).vencida).length;

  /* A data da fibra muda pela Nova data: depois do Técnico não veio (a data da
     Claro) ou quando o cliente respondeu à BKO na conversa do pedido (Douglas
     30/10/2026, maquete aprovada). A régua mora em fn_em_instalacao_reagendar. */
  const novaData = useMutation({
    mutationFn: async ({ pedido, agenda, motivo }: { pedido: PedidoInstalacao; agenda: string; motivo?: string }) => {
      const rpc = supabase.rpc.bind(supabase) as unknown as Rpc;
      const { error } = await rpc('fn_em_instalacao_reagendar', { p_itens: pedido.itens.map((i) => i.item_id), p_data: agenda, p_motivo: motivo ?? '' });
      if (error) throw error;
    },
    onSuccess: (_r, { pedido }) => {
      toast({ title: 'Nova data lançada', description: 'O cliente recebe a nova data e a antiga fica na timeline do pedido.' });
      setNovaDataDe(null);
      queryClient.invalidateQueries({ queryKey: ['instalacao-acompanhamento'] });
      releFila(pedido.pedido_id);
    },
    onError: (e) => toast({ title: 'A nova data não entrou', description: e instanceof Error ? e.message : 'Tente novamente.', variant: 'destructive' }),
  });

  const tecnicoNaoVeio = useMutation({
    mutationFn: (pedido: PedidoInstalacao) =>
      chamarInstalacao<{ id: number; ficha: boolean; erro: string | null }>(`${pedido.pedido_id}/tecnico-nao-veio`, 'POST', {}),
    onSuccess: (r, pedido) => {
      toast(r.ficha
        ? { title: 'Técnico não veio registrado', description: 'A ficha foi para o grupo PRIME CLARO. Quando a Claro marcar, lance a nova data.' }
        : { title: 'Técnico não veio registrado', description: `A ficha não saiu para o grupo: ${r.erro || 'o CRM tenta de novo sozinho'}.`, variant: 'destructive' });
      queryClient.invalidateQueries({ queryKey: ['instalacao-acompanhamento'] });
      releFila(pedido.pedido_id);
    },
    onError: (e) => toast({ title: 'Não registrou', description: e instanceof Error ? e.message : 'Tente novamente.', variant: 'destructive' }),
  });

  const abrirConversa = async (pedidoId: string) => {
    try {
      const c = await queryClient.fetchQuery({ queryKey: ['pedido-contato', pedidoId], queryFn: () => fetchPedidoContato(pedidoId), staleTime: 5 * 60_000 });
      if (c?.urlConversa) window.open(c.urlConversa, '_blank');
      else toast({ title: 'Conversa do pedido não encontrada', description: 'Vincule a conversa do cliente no Painel do Pedido.' });
    } catch {
      toast({ title: 'Não abriu a conversa', description: 'Tente novamente.', variant: 'destructive' });
    }
  };

  const instalado = useMutation({
    mutationFn: async (pedido: PedidoInstalacao) => {
      /* Uma linha por vez, de propósito: a triagem-ativacao manda a boas-vindas
         e abre o ciclo da Qualidade. Em paralelo, não está provado que duas
         linhas do mesmo pedido não mandem a boas-vindas duas vezes. */
      for (const it of pedido.itens) {
        const { error } = await supabase.functions.invoke('triagem-ativacao', {
          body: {
            item_id: it.item_id, pedido_id: it.pedido_id, cnpj: it.cnpj, razao_social: it.razao_social,
            telefone: it.telefone, email: it.email, cliente_id: it.cliente_id,
            status_anterior_id: it.status_id, coluna_anterior: it.coluna_atual, quadro_anterior: it.quadro_atual,
            acao: 'finalizar_ativo',
          },
        });
        if (error) throw new Error(await mensagemDaFuncao(error, 'Tente novamente.'));
      }
      return pedido.itens.length;
    },
    onSuccess: (n, pedido) => { toast({ title: 'Instalado', description: n > 1 ? `${n} itens do pedido em Finalizado Pedido (100%).` : 'Pedido em Finalizado Pedido (100%).' }); depois(pedido.pedido_id, 'sai'); },
    onError: (e) => toast({ title: 'Não finalizou', description: e instanceof Error ? e.message : 'Tente novamente.', variant: 'destructive' }),
  });

  const manterNoFim = useMutation({
    /* A justificativa já foi gravada pelo ManterComNotaModal; aqui o manter_em
       de todas as linhas numa ida só (`fn_triagem_manter`). */
    mutationFn: (pedido: PedidoInstalacao) => manterNaTriagem({
      pedidoId: pedido.pedido_id,
      itens: pedido.itens.map((i) => i.item_id),
      coluna: 'EM INSTALAÇÃO',
    }),
    onSuccess: (_r, pedido) => { toast({ title: 'Mantido', description: 'A justificativa ficou registrada na timeline do pedido.' }); depois(pedido.pedido_id, 'desce'); },
    onError: (e) => toast({ title: 'Não manteve', description: e instanceof Error ? e.message : 'Tente novamente.', variant: 'destructive' }),
  });


  return (
    <>
      <TriagemShell
        isOpen={isOpen}
        onFechar={onClose}
        Icone={Wrench}
        titulo="Triagem Em instalação"
        contadores={{ pedidos: pedidos.length, itens: pedidos.reduce((s, p) => s + p.itens.length, 0), valor: 0 }}
        cabecalhoExtra={
          <div className="flex items-center gap-2">
            <span className={cn('whitespace-nowrap rounded-lg border border-border bg-card px-3 py-1 text-[11px]', vencidas ? 'text-primary' : 'text-foreground')}>
              {vencidas} fora do prazo
            </span>
            <div className="flex h-7 items-center gap-0.5 rounded-lg bg-[#EDE8E0] p-0.5 dark:bg-[#161010]">
              {([['fibra', `Fibra ${fibra.length}`], ['tt', `TT Fixa ${tt.length}`]] as const).map(([k, rotulo]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setAba(k)}
                  className={cn('h-6 whitespace-nowrap rounded-md px-2.5 text-[11px] font-semibold',
                    aba === k ? 'bg-[#5B0A0D] text-white dark:bg-[#9D2530]' : 'text-muted-foreground hover:text-foreground')}
                >
                  {rotulo}
                </button>
              ))}
            </div>
          </div>
        }
        segmento={segmento}
        onSegmento={setSegmento}
        consulta={{ chave: 'em_instalacao', pedidoIds: pedidos.map(p => p.pedido_id) }}
        sla={null}
        fifo={false}
        legenda={aba === 'fibra' ? [
          { key: 'whatsapp', Icone: MessageSquare, label: 'WhatsApp · conversa do pedido' },
          { key: 'instalado', Icone: Check, label: 'Instalado 100%' },
          { key: 'nao-veio', Icone: UserX, label: 'Técnico não veio' },
          { key: 'nova-data', Icone: CalendarClock, label: 'Nova data · o cliente respondeu ou a Claro remarcou' },
          { key: 'analisar', Icone: Eye, label: 'Analisar · quando travar' },
          { key: 'cancelar', Icone: X, label: 'Pedido cancelado' },
        ] : [
          { key: 'manter', Icone: Clock, label: 'Justificar e manter' },
          { key: 'instalado', Icone: Check, label: 'Migrou · Finalizado 100%' },
          { key: 'email-tt', Icone: Mail, label: 'TT Fixa · e-mail à gerente' },
          { key: 'analisar', Icone: Eye, label: 'Analisar · Pedido 360' },
          { key: 'cancelar', Icone: X, label: 'Pedido cancelado' },
        ]}
        isLoading={isLoading}
        onAtualizar={async () => { await refetch(); toast({ title: 'Dados atualizados', duration: 1500 }); }}
        vazio={lista.length === 0 ? { titulo: 'Nenhuma instalação nesta fila', descricao: 'As fixas entram aqui pelo Inputar, com a data e o turno da instalação.' } : undefined}
      >
        {aba === 'fibra' && placar && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-border px-3 py-2 text-[11px] text-muted-foreground">
            <span className="font-semibold text-foreground">Placar do mês</span>
            <span>Agendadas <b className="tabular-nums text-foreground">{placar.agendadas}</b></span>
            <span>Instalou no 1º turno <b className="tabular-nums text-foreground">{placar.instalou_1o_turno}</b></span>
            <span>Técnico não veio <b className="tabular-nums text-foreground">{placar.tecnico_nao_veio}</b></span>
            <span>Nova data pelo cliente <b className="tabular-nums text-foreground">{placar.nova_data_cliente ?? 0}</b></span>
            <span>BKO falou com o cliente em <b className="tabular-nums text-foreground">{placar.bko_falou_min == null ? '—' : `${placar.bko_falou_min} min`}</b></span>
          </div>
        )}
        <table className="w-full" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '46px' }} /><col style={{ width: '132px' }} /><col /><col style={{ width: '92px' }} />
            <col style={{ width: '92px' }} /><col style={{ width: '108px' }} /><col style={{ width: '130px' }} />
            <col style={{ width: aba === 'fibra' ? '250px' : '190px' }} /><col style={{ width: '228px' }} />
          </colgroup>
          <thead className="sticky top-0 z-10 bg-surface">
            <tr>
              <th className={TRIAGEM_TH}>Fila</th>
              <th className={TRIAGEM_TH}>{rotuloSegmento(segmento)}</th>
              <th className={TRIAGEM_TH}>Razão social</th>
              <th className={TRIAGEM_TH}>Consultor</th>
              <th className={TRIAGEM_TH}>Pedido</th>
              <th className={TRIAGEM_TH}>Produto</th>
              {aba === 'fibra'
                ? <th className={TRIAGEM_TH} title="Data e turno da instalação que a BKO lançou no Inputar.">Instalação</th>
                : <th className={TRIAGEM_TH} title="Dia em que a TT Fixa chegou na fila. Ela não tem agenda de instalação.">Chegada</th>}
              <th className={TRIAGEM_TH} title={aba === 'fibra' ? 'O turno da instalação: o técnico tem 1 h depois do fim; com 2 h sem tratar, a BKO trava' : 'Horas de segunda a sexta desde a chegada. O prazo da TT Fixa é 48 h.'}>Prazo</th>
              <th className={TRIAGEM_TH}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((p, idx) => {
              const ag = situacaoPedido(p);
              const faltaTt = p.soTt && !estadoTt.get(p.pedido_id)?.enviados ? faltaParaEmailTt(dadosTt.get(p.pedido_id)) : [];
              const est = estadoFibra.get(p.pedido_id);
              const turnoAbriu = !!p.agenda && new Date(est?.agenda ?? p.agenda).getTime() <= Date.now();
              const primeiro = p.itens[0];
              const produtos = [...new Set(p.itens.map((i) => i.produto))].join(' · ');
              const ocupado = ocupada(p.pedido_id);
              return (
                <tr
                  key={p.pedido_id}
                  className={cn(TRIAGEM_TR_BASE, TRIAGEM_TR_LIVRE,
                    idx % 2 === 1 && TRIAGEM_TR_ZEBRA,
                    ag.vencida && 'shadow-[inset_3px_0_0_hsl(var(--primary))]')}
                >
                  <td className={TRIAGEM_TD}>
                    <span className="text-[10px] font-bold tabular-nums text-muted-foreground">{idx + 1}º</span>
                  </td>
                  <td className={TRIAGEM_TD}>
                    <CopyOnClick value={primeiro.documento} className="max-w-full text-[11px] tabular-nums text-muted-foreground">
                      {formatarDocumento(primeiro.documento)}
                    </CopyOnClick>
                  </td>
                  <td className={TRIAGEM_TD}>
                    <span className={TRIAGEM_CELULA_FORTE} title={primeiro.razao_social}>{primeiro.razao_social || '-'}</span>
                  </td>
                  <td className={TRIAGEM_TD}>
                    <span className={TRIAGEM_CELULA} title={primeiro.consultor}>{primeiro.consultor || '-'}</span>
                  </td>
                  <td className={TRIAGEM_TD}>
                    <button
                      type="button"
                      className="block max-w-full truncate text-[11px] font-bold tabular-nums text-primary hover:underline dark:text-[#C87484]"
                      title="Abrir Painel do Pedido"
                      onClick={() => abrirPedido(p.pedido_id, 'visao360')}
                    >
                      {primeiro.numero_pedido}
                    </button>
                  </td>
                  <td className={TRIAGEM_TD}>
                    <span className={TRIAGEM_CELULA} title={produtos}>{produtos || '-'}</span>
                  </td>
                  <td className={TRIAGEM_TD}>
                    <span className={TRIAGEM_CELULA_NUM} title={ag.texto}>{ag.texto}</span>
                  </td>
                  <td className={TRIAGEM_TD}>
                    {p.soTt ? (() => {
                      const sla = prazosSla?.[p.pedido_id];
                      const enviados = estadoTt.get(p.pedido_id)?.enviados ?? 0;
                      const estado = faltaTt.length ? `falta ${faltaTt.join(' e ')}` : enviados ? `${enviados}º e-mail enviado` : 'sem e-mail à gerente';
                      const estourou = sla ? sla.estourado : ag.vencida;
                      return (
                        <span className="block min-w-0 leading-tight" title={`${estado} · ${sla ? `${rotuloPrazo(sla.restam_min)} · prazo da fila: ${sla.prazo}, conta da chegada ou do último e-mail` : ag.rotulo}${faltaTt.length ? ' · o e-mail só sai com o contrato PF e o endereço completo' : ''}`}>
                          <span className={cn('block truncate text-[11px] font-bold', faltaTt.length || !enviados ? 'text-primary dark:text-[#C87484]' : 'text-foreground')}>{estado}</span>
                          <span className={cn('flex items-center gap-1 truncate text-[10.5px] tabular-nums', estourou ? 'font-bold text-danger' : 'text-muted-foreground')}>
                            {estourou && <span className="h-[5px] w-[5px] flex-shrink-0 rounded-full bg-danger" />}
                            {sla ? rotuloPrazo(sla.restam_min) : ag.rotulo}
                          </span>
                        </span>
                      );
                    })() : (() => {
                      const f = situacaoFibra(p, estadoFibra.get(p.pedido_id));
                      return (
                        <span className="block min-w-0 leading-tight" title={`${f.principal} · ${f.detalhe}`}>
                          <span className={cn('flex items-center gap-1 truncate text-[11px] font-bold', TOM_FIBRA[f.tom])}>
                            {(f.tom === 'danger' || f.vencida) && <span className={cn('h-[5px] w-[5px] flex-shrink-0 rounded-full', f.tom === 'danger' ? 'bg-danger' : 'bg-primary dark:bg-[#C87484]')} />}
                            <span className="truncate">{f.principal}</span>
                          </span>
                          <span className="block truncate text-[10.5px] tabular-nums text-muted-foreground">{f.detalhe}</span>
                        </span>
                      );
                    })()}
                  </td>
                  <td className={TRIAGEM_TD}>
                    <AcoesTriagem
                      manter={p.soTt &&
                        <BotaoManter disabled={ocupado}
                          carregando={ocupado && manterNoFim.isPending && manterNoFim.variables?.pedido_id === p.pedido_id}
                          title="Manter — consultei e a instalação não mudou; justificar e mandar para o fim da fila"
                          onClick={() => setManter(p)} />
                      }
                      avancar={
                        <Button size="icon" variant="ghost" className={TRIAGEM_BTN_PRIMARIO} disabled={ocupado}
                          title={p.soTt ? 'Migrou para o CNPJ — finalizar o pedido (Finalizado 100%)' : 'Instalado — finalizar o pedido (Finalizado 100%)'}
                          onClick={() => naLinha(p.pedido_id, () => instalado.mutateAsync(p))}>
                          {ocupado && instalado.isPending && instalado.variables?.pedido_id === p.pedido_id
                            ? <Loader2 className="animate-spin" /> : <Check />}
                        </Button>
                      }
                      desvios={p.soTt ? (() => {
                        const est = estadoTt.get(p.pedido_id);
                        const proximo = (est?.enviados ?? 0) + 1;
                        const titulo = proximo === 1 ? 'Enviar o 1º e-mail à gerente de contas' : `Ainda não migrou — enviar o ${proximo}º e-mail (cobrança)`;
                        const descricao = (proximo === 1
                          ? 'Sai pela caixa atendimento@ para a gerente de contas, pedindo a migração da fibra de PF para o CNPJ, com o contrato PF, o endereço e o móvel vendido junto.'
                          : `Já foram ${proximo - 1} e-mail(s)${est?.ultimo_em ? `, o último em ${new Date(est.ultimo_em).toLocaleDateString('pt-BR', { timeZone: TZ })}` : ''}. O ${proximo}º diz que é cobrança e cita os anteriores.`)
                          + ' O pedido vai para o fim da fila e o prazo recomeça.';
                        return (
                          <TtEmailPopover
                            aberto={emailDe === p.pedido_id}
                            onAberto={(o) => setEmailDe(o ? p.pedido_id : null)}
                            titulo={titulo}
                            descricao={descricao}
                            proximo={proximo}
                            dados={dadosTt.get(p.pedido_id)}
                            salvando={false}
                            enviando={ocupado && emailTt.isPending}
                            onEnviar={(novos) => naLinha(p.pedido_id, () => emailTt.mutateAsync({ pedido: p, novos }))}
                          >
                            <Button size="icon" variant="ghost" className={TRIAGEM_BTN} disabled={ocupado} title={titulo}>
                              {ocupado && emailTt.isPending && emailTt.variables?.pedido.pedido_id === p.pedido_id ? <Loader2 className="animate-spin" /> : <Mail />}
                            </Button>
                          </TtEmailPopover>
                        );
                      })() : (
                        <>
                          {!est?.tecnico_nao_veio_em && (
                            <Button size="icon" variant="ghost" className={TRIAGEM_BTN} disabled={ocupado || !turnoAbriu}
                              title={turnoAbriu ? 'Técnico não veio — a ficha vai para o grupo PRIME CLARO' : 'Técnico não veio — libera quando o turno abrir'}
                              onClick={() => setNaoVeioDe(p)}>
                              {ocupado && tecnicoNaoVeio.isPending && tecnicoNaoVeio.variables?.pedido_id === p.pedido_id ? <Loader2 className="animate-spin" /> : <UserX />}
                            </Button>
                          )}
                          {(() => {
                            const peloCliente = !est?.tecnico_nao_veio_em && !!est?.conversa_cliente_em;
                            const libera = !!est?.tecnico_nao_veio_em || peloCliente;
                            const titulo = est?.tecnico_nao_veio_em
                              ? 'Nova data da Claro — lançar a data e o turno que a Claro marcou'
                              : peloCliente
                                ? 'Nova data — o cliente respondeu: lançar a data que ele pediu'
                                : est?.conversa_bko_em
                                  ? 'Nova data — libera quando o cliente responder na conversa do pedido'
                                  : 'Nova data — fale com o cliente na conversa do pedido; libera quando ele responder';
                            return (
                              <Button size="icon" variant="ghost" className={peloCliente ? TRIAGEM_BTN_ATIVO : TRIAGEM_BTN} disabled={ocupado || !libera}
                                title={titulo}
                                onClick={() => { setNovaDataPeloCliente(peloCliente); setNovaDataDe(p); }}>
                                {ocupado && novaData.isPending && novaData.variables?.pedido.pedido_id === p.pedido_id ? <Loader2 className="animate-spin" /> : <CalendarClock />}
                              </Button>
                            );
                          })()}
                        </>
                      )}
                      ferramentas={p.soTt ? (
                        <Button size="icon" variant="ghost" className={TRIAGEM_BTN}
                          title="Analisar — Abrir Painel do Pedido (Pedido 360)"
                          onClick={() => abrirPedido(p.pedido_id, 'visao360')}>
                          <Eye className="h-3 w-3" />
                        </Button>
                      ) : (
                        <>
                          <Button size="icon" variant="ghost" className={TRIAGEM_BTN}
                            title="WhatsApp — abrir a conversa do pedido com o cliente"
                            onClick={() => void abrirConversa(p.pedido_id)}>
                            <MessageSquare />
                          </Button>
                          {est?.travou_em && (
                            <Button size="icon" variant="ghost" className={TRIAGEM_BTN}
                              title="Analisar — Abrir Painel do Pedido (Pedido 360)"
                              onClick={() => abrirPedido(p.pedido_id, 'visao360')}>
                              <Eye className="h-3 w-3" />
                            </Button>
                          )}
                        </>
                      )}
                      cancelar={
                        <BotaoCancelar disabled={ocupado}
                          title="Pedido cancelado — abrir o cancelamento do item"
                          onClick={() => setCancelar(primeiro)} />
                      }
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TriagemShell>

      {cancelar && (
        <CancelarItemModal
          open={!!cancelar}
          onOpenChange={(o) => !o && setCancelar(null)}
          itemId={cancelar.item_id}
          pedidoId={cancelar.pedido_id}
          itemNome={`${cancelar.razao_social} - ${cancelar.numero_pedido}`}
          onSuccess={() => { setCancelar(null); refetch(); }}
        />
      )}

      <InputarFibraDialog
        porta={novaDataPeloCliente ? 'nova_data_cliente' : 'nova_data'}
        conversa={(() => {
          const est = novaDataDe ? estadoFibra.get(novaDataDe.pedido_id) : undefined;
          return est?.conversa_cliente_em
            ? { bkoEm: est.conversa_bko_em, bkoTexto: est.conversa_bko_texto, clienteEm: est.conversa_cliente_em, clienteTexto: est.conversa_cliente_texto ?? '' }
            : null;
        })()}
        pedido={novaDataDe && {
          pedido_id: novaDataDe.pedido_id,
          razao_social: novaDataDe.itens[0]?.razao_social ?? '',
          itens: novaDataDe.itens.map((i) => ({ produto: i.produto, numero_cotacao_operadora: i.contrato })),
        }}
        isLoading={!!novaDataDe && ocupada(novaDataDe.pedido_id)}
        onOpenChange={(o) => { if (!o) setNovaDataDe(null); }}
        onConfirm={(agenda, motivo) => { const alvo = novaDataDe; if (alvo) naLinha(alvo.pedido_id, () => novaData.mutateAsync({ pedido: alvo, agenda, motivo })); }}
      />

      <AlertDialog open={!!naoVeioDe} onOpenChange={(o) => { if (!o) setNaoVeioDe(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Técnico não veio</AlertDialogTitle>
            <AlertDialogDescription>
              {naoVeioDe?.itens[0]?.razao_social} · {agendaInstalacaoTexto(naoVeioDe?.agenda ?? null)}.
              A ficha vai para o grupo PRIME CLARO e a fibra fica esperando a nova data da Claro, sem trava.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const alvo = naoVeioDe; setNaoVeioDe(null); if (alvo) naLinha(alvo.pedido_id, () => tecnicoNaoVeio.mutateAsync(alvo)); }}>
              Registrar e mandar a ficha
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {manter && (
        <ManterComNotaModal
          open={!!manter}
          onOpenChange={(o) => !o && setManter(null)}
          pedidoId={manter.pedido_id}
          clienteId={manter.itens[0].cliente_id}
          onConfirm={() => { const alvo = manter; naLinha(alvo.pedido_id, () => manterNoFim.mutateAsync(alvo)); setManter(null); }}
          justificarFila={{
            itemId: manter.itens[0].item_id,
            coluna: 'EM INSTALAÇÃO',
            numeroPedido: manter.itens[0].numero_pedido,
            razaoSocial: manter.itens[0].razao_social,
            justificativaAtual: null,
            diasParado: Math.max(0, Math.floor((Date.now() - new Date(manter.itens[0].entrada_coluna_em).getTime()) / 86_400_000)),
          }}
        />
      )}
    </>
  );
}
